<template>

  <div class="position-relative">

    <!-- ================= MENU: CHOOSE A SECTION ================= -->
    <b-row v-if="!activeSection" class="mt-4">
      <b-col cols="12" md="4" class="mb-4">
        <b-card class="text-center config-card h-100" no-body @click="activeSection = 'category'">
          <div class="py-4 d-flex flex-column align-items-center">
            <i class="fas fa-layer-group fa-3x mb-3 text-primary"></i>
            <h5 class="card-title mb-0">Fee Stucture</h5>
            <small class="text-muted mt-1">Define fee categories and the components that make them up.</small>
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" md="4" class="mb-4">
        <b-card class="text-center config-card h-100" no-body @click="activeSection = 'order'">
          <div class="py-4 d-flex flex-column align-items-center">
            <i class="fas fa-sort-numeric-down fa-3x mb-3 text-success"></i>
            <h5 class="card-title mb-0">Fee Collection Order</h5>
            <small class="text-muted mt-1">Set the priority in which fee components are settled when a student pays
              less than the full amount due.</small>
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" md="4" class="mb-4">
        <b-card class="text-center config-card h-100" no-body @click="activeSection = 'components'">
          <div class="py-4 d-flex flex-column align-items-center">
            <i class="fas fa-cubes fa-3x mb-3 text-info"></i>
            <h5 class="card-title mb-0">Fee Components</h5>
            <small class="text-muted mt-1">Manage the shared catalog of fee components used across structures.</small>
          </div>
        </b-card>
      </b-col>

      <b-col v-if="isClassBilling" cols="12" md="4" class="mb-4">
        <b-card class="text-center config-card h-100" no-body @click="activeSection = 'classes'">
          <div class="py-4 d-flex flex-column align-items-center">
            <i class="fas fa-chalkboard-teacher fa-3x mb-3 text-warning"></i>
            <h5 class="card-title mb-0">Class Fee Structures</h5>
            <small class="text-muted mt-1">Assign a fee structure to each class. Students are mapped to it
              automatically when added to the class.</small>
          </div>
        </b-card>
      </b-col>

    </b-row>

    <!-- ================= FEE CATEGORY PAGE ================= -->
    <b-card v-else-if="activeSection === 'category'" class="mt-4">
      <!-- Add Category Button -->
      <b-button variant="info" @click="showCategoryModal(null)">
        <i class="fas fa-plus-square text-white" style="font-size: 13px;"></i> Add Fee Structure
      </b-button>

      <!-- Fee Category Modal -->
      <b-modal id="CategoryModal" hide-footer centered :size="isEditingCategory ? 'md' : 'lg'">
        <div class="p-3 border rounded">
          <!-- Editing an existing category: name only, components managed inline in the table -->
          <template v-if="isEditingCategory">
            <b-form-group>
              <label>Structure Name</label><span class="text-danger">*</span>
              <b-form-input v-model.trim="categoryForm.name" placeholder="e.g. Merit, Management"
                @input="categoryNameError = null" />
              <span class="input-error-message" v-if="!$v.categoryForm.name.required && $v.categoryForm.name.$dirty">
                Structure name is required!
              </span>
              <span class="input-error-message" v-if="categoryNameError">{{ categoryNameError }}</span>
            </b-form-group>
            <b-button variant="info" @click="saveCategory">Update Structure</b-button>
          </template>

          <!-- Creating a new category: category and its components all in one form -->
          <template v-else>
            <b-form-group>
              <label>Structure Name</label><span class="text-danger">*</span>
              <b-form-input v-model.trim="newCategoryForm.name" placeholder="e.g. Merit, Management"
                @input="newCategoryNameError = null" />
              <span class="input-error-message" v-if="newCategoryNameError">{{ newCategoryNameError }}</span>
            </b-form-group>

            <hr />
            <label class="mb-0 font-weight-bold">Fee Components</label>

            <div v-if="catalogComponents.length" class="catalog-pick p-2 mt-2">
              <small class="text-muted d-block mb-1">Select components, then fill in their details below</small>
              <b-form-checkbox v-for="item in catalogComponents" :key="item.id" inline
                :checked="isCatalogPicked(newCategoryForm.components, item)"
                @change="(checked) => toggleCatalogPick(newCategoryForm.components, item, checked)">
                {{ item.name }}
              </b-form-checkbox>
            </div>
            <div v-else class="text-muted font-italic mt-2">
              No components yet. Add them from the Fee Components catalog first.
            </div>

            <div v-for="(component, cIndex) in newCategoryForm.components" :key="component.catalog.id"
              class="component-box p-2 mt-2">
              <div class="component-box-header mb-2">
                <strong class="component-box-title">{{ component.catalog.name }}</strong>
                <i class="fas fa-trash text-danger component-box-remove" style="cursor: pointer;font-size: 14px !important;"
                  @click="newCategoryForm.components.splice(cIndex, 1)"></i>
              </div>
              <b-row class="align-items-end">
                <b-col cols="12" :md="isClassBilling ? 3 : 4">
                  <b-form-group label="Type" label-class="font-weight-bold" class="mb-2">
                    <b-form-select v-model="component.component_type" :options="componentTypeOptions" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" :md="isClassBilling ? 3 : 4">
                  <b-form-group label="Billing Frequency" label-class="font-weight-bold" class="mb-2">
                    <b-form-select v-model="component.billing_frequency" :options="billingFrequencyOptions" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" :md="isClassBilling ? 3 : 4">
                  <b-form-group label="Partial Payment" label-class="font-weight-bold" class="mb-2">
                    <div class="partial-payment-check">
                      <b-form-checkbox v-model="component.allow_partial_payment">
                        Allow partial payment
                      </b-form-checkbox>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col v-if="isClassBilling" cols="12" md="3">
                  <b-form-group label="Auto Mapping" label-class="font-weight-bold" class="mb-2">
                    <div class="partial-payment-check">
                      <b-form-checkbox v-model="component.allow_auto_mapping">
                        Allow auto mapping
                      </b-form-checkbox>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <b-button variant="info" class="mt-3" @click="saveNewCategoryWithComponents">
              Save Structure
            </b-button>
          </template>
        </div>
      </b-modal>

      <!-- Fee Component Modal -->
      <b-modal id="ComponentModal" hide-footer centered :size="isEditingComponent ? 'md' : 'lg'">
        <div class="p-3 border rounded">
          <h6 v-if="activeCategory">
            {{ isEditingComponent ? 'Edit' : 'Add' }} Fee Component for:
            <strong>{{ activeCategory.name }}</strong>
          </h6>

          <!-- Adding: several components at once, each in its own box -->
          <template v-if="!isEditingComponent">
            <label class="mb-0 font-weight-bold">Fee Components</label>

            <div v-if="catalogNotInActiveCategory.length" class="catalog-pick p-2 mt-2">
              <small class="text-muted d-block mb-1">Select components, then fill in their details below</small>
              <b-form-checkbox v-for="item in catalogNotInActiveCategory" :key="item.id" inline
                :checked="isCatalogPicked(newComponentRows, item)"
                @change="(checked) => toggleCatalogPick(newComponentRows, item, checked)">
                {{ item.name }}
              </b-form-checkbox>
            </div>
            <div v-else class="text-muted font-italic mt-2">
              This structure already has every catalog component. Add new ones from the Fee Components catalog.
            </div>

            <div v-for="(component, cIndex) in newComponentRows" :key="component.catalog.id"
              class="component-box p-2 mt-2">
              <div class="component-box-header mb-2">
                <strong class="component-box-title">{{ component.catalog.name }}</strong>
                <i class="fas fa-trash text-danger component-box-remove"
                  style="cursor: pointer;font-size: 14px !important;" @click="newComponentRows.splice(cIndex, 1)"></i>
              </div>
              <b-row class="align-items-end">
                <b-col cols="12" :md="isClassBilling ? 3 : 4">
                  <b-form-group label="Type" label-class="font-weight-bold" class="mb-2">
                    <b-form-select v-model="component.component_type" :options="componentTypeOptions" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" :md="isClassBilling ? 3 : 4">
                  <b-form-group label="Billing Frequency" label-class="font-weight-bold" class="mb-2">
                    <b-form-select v-model="component.billing_frequency" :options="billingFrequencyOptions" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" :md="isClassBilling ? 3 : 4">
                  <b-form-group label="Partial Payment" label-class="font-weight-bold" class="mb-2">
                    <div class="partial-payment-check">
                      <b-form-checkbox v-model="component.allow_partial_payment">
                        Allow partial payment
                      </b-form-checkbox>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col v-if="isClassBilling" cols="12" md="3">
                  <b-form-group label="Auto Mapping" label-class="font-weight-bold" class="mb-2">
                    <div class="partial-payment-check">
                      <b-form-checkbox v-model="component.allow_auto_mapping">
                        Allow auto mapping
                      </b-form-checkbox>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>

            <b-button variant="info" class="mt-3" :disabled="componentSaving" @click="saveNewComponents">
              Save Components
            </b-button>
          </template>

          <!-- Editing a single existing component -->
          <template v-else>
            <b-form-group>
              <label class="font-weight-bold">Component</label>
              <div>{{ componentForm.name }}</div>
              <small class="text-muted">
                Renaming is done from the Fee Components catalog, since this component may be shared with other
                structures.
              </small>
            </b-form-group>
            <b-form-group label="Component Type" label-class="font-weight-bold">
              <b-form-select v-model="componentForm.component_type" :options="componentTypeOptions"
                class="form-control-sm" />
              <span class="input-error-message"
                v-if="!$v.componentForm.component_type.required && $v.componentForm.component_type.$dirty">
                Component type is required!
              </span>
            </b-form-group>
            <b-form-group label="Billing Frequency" label-class="font-weight-bold">
              <b-form-select v-model="componentForm.billing_frequency" :options="billingFrequencyOptions"
                class="form-control-sm" />
              <span class="input-error-message"
                v-if="!$v.componentForm.billing_frequency.required && $v.componentForm.billing_frequency.$dirty">
                Billing frequency is required!
              </span>
            </b-form-group>
            <b-form-group label="Partial Payment" label-class="font-weight-bold">
              <b-form-checkbox v-model="componentForm.allow_partial_payment">
                Allow partial payment
              </b-form-checkbox>
            </b-form-group>
            <b-form-group v-if="isClassBilling" label="Auto Mapping" label-class="font-weight-bold"
              description="Only components with this allowed can be billed from Class Fee Structures.">
              <b-form-checkbox v-model="componentForm.allow_auto_mapping">
                Allow auto mapping
              </b-form-checkbox>
            </b-form-group>
            <b-button variant="info" @click="saveComponent">Update Component</b-button>
          </template>
        </div>
      </b-modal>

      <!-- Table Section -->
      <div class="table-responsive rounded mt-3">
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <tr>
              <th>#</th>
              <th>Fee Structure</th>
              <th>Component</th>
              <th>Component Type</th>
              <th>Billing Frequency</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in feeCategoryComponentRows" :key="row.key">
              <td v-if="row.isFirstOfCategory" :rowspan="row.rowspan">
                <div class="d-flex align-items-center">
                  <span>{{ row.categoryIndex }}</span>

                  <div v-if="row.category.studentcategorymappings.length != 0"
                    class="ms-2 tooltip-wrapper d-inline-flex align-items-center">
                    <b-icon icon="lock-fill" variant="danger"></b-icon>
                    <div class="custom-tooltip">
                      <span>Mapped</span>
                    </div>
                  </div>
                </div>
              </td>

              <td v-if="row.isFirstOfCategory" :rowspan="row.rowspan">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <span>{{ row.category.name }}</span>
                    <small v-if="row.category.description" class="text-muted d-block">
                      {{ row.category.description }}
                    </small>
                  </div>
                  <span v-if="row.category.studentcategorymappings.length == 0">
                    <i class="fas fa-plus-circle text-success mx-1" style="cursor: pointer;font-size: 15px !important;"
                      @click="showComponentModal(row.category, null)"></i>
                    <i class="fas fa-edit text-info mx-1" style="cursor: pointer;font-size: 15px !important;"
                      @click="showCategoryModal(row.category)"></i>
                    <i class="fas fa-trash text-danger mx-1" style="cursor: pointer;font-size: 15px !important;"
                      @click="removeCategory(row.category.id)"></i>
                  </span>
                </div>
              </td>

              <template v-if="row.component">
                <td>
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      {{ row.component.name }}
                      <small v-if="row.component.description" class="text-muted d-block">
                        {{ row.component.description }}
                      </small>
                    </div>
                    <span v-if="row.category.studentcategorymappings.length == 0">
                      <i class="fas fa-edit text-info mx-1" style="cursor: pointer;font-size: 15px !important;"
                        @click="showComponentModal(row.category, row.component)"></i>
                      <i class="fas fa-trash text-danger mx-1" style="cursor: pointer;font-size: 15px !important;"
                        @click="removeComponent(row.category.id, row.component.id)"></i>
                    </span>
                  </div>
                </td>
                <td>
                  <b-badge :variant="typeBadgeVariant(row.component.component_type)" class="text-capitalize">
                    {{ row.component.component_type }}
                  </b-badge>
                </td>
                <td class="text-capitalize">{{ row.component.billing_frequency }}</td>
              </template>
              <td v-else colspan="3" class="text-muted font-italic">No components available.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <!-- ================= FEE COLLECTION ORDER PAGE ================= -->
    <b-card v-else-if="activeSection === 'order'" class="mt-4">
      <b-form-group label="How should fees be collected?" label-class="font-weight-bold">
        <b-form-radio-group v-model="feeCollectionMode" stacked @change="saveCollectionMode">
          <b-form-radio value="ordered">
            Collect fees in the preset order
            <small class="text-muted d-block">
              When a student pays less than the full amount due, the amount is applied to components automatically,
              top-to-bottom, following the order set below.
            </small>
          </b-form-radio>
          <b-form-radio value="manual" class="mt-2">
            User can collect fee for seperate heads
            <small class="text-muted d-block">
              The amount to apply to each component is entered manually at the time of collection.
            </small>
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>

      <p class="text-muted">
        Drag components to set the order fees are collected in. This order is used when "Collect fees in the present
        order" is selected above.
      </p>

      <div v-if="!feeCategories.length"
        class="coming-soon-box d-flex flex-column align-items-center justify-content-center text-center py-5">
        <i class="fas fa-info-circle fa-2x text-muted mb-2"></i>
        <span class="text-muted">No fee categories available yet.</span>
      </div>

      <b-row v-else>
        <b-col cols="12" md="6" v-for="category in feeCategories" :key="category.id" class="mb-4">
          <b-card class="h-100 order-tile">
            <h6 class="mb-3">{{ category.name }}</h6>

            <div v-if="!category.components.length" class="text-muted font-italic">No components available.</div>

            <draggable v-else :list="category.components" handle=".drag-handle"
              @start="beforeDragOrder = category.components.map((c) => c.id)"
              @end="saveComponentOrder(category)">
              <div v-for="component in category.components" :key="component.id"
                class="d-flex align-items-center drag-handle order-item px-2 py-2 mb-2">
                <i class="fas fa-grip-vertical text-muted mr-2"></i>
                <span>{{ component.name }} – {{ formatComponentAmount(component) }}</span>
              </div>
            </draggable>
          </b-card>
        </b-col>
      </b-row>
    </b-card>

    <!-- ================= FEE COMPONENTS CATALOG PAGE ================= -->
    <b-card v-else-if="activeSection === 'components'" class="mt-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 class="mb-0">Fee Components Catalog</h6>
          <small class="text-muted">Shared components any fee structure can map in.</small>
        </div>
        <b-button variant="info" size="sm" @click="showCatalogModal(null)">
          <i class="fas fa-plus-square text-white" style="font-size: 13px;"></i> Add Component
        </b-button>
      </div>

      <!-- Catalog Component Modal -->
      <b-modal id="CatalogComponentModal" hide-footer centered>
        <div class="p-3 border rounded">
          <!-- Editing: one existing component -->
          <template v-if="catalogForm.id">
            <b-form-group>
              <label class="font-weight-bold">Component Name</label><span class="text-danger">*</span>
              <b-form-input v-model.trim="catalogForm.name" placeholder="e.g. Transport"
                @input="catalogNameError = null" />
              <span class="input-error-message" v-if="catalogNameError">{{ catalogNameError }}</span>
            </b-form-group>
            <b-button variant="info" @click="saveCatalogComponent">Update Component</b-button>
          </template>

          <!-- Adding: several components at once -->
          <template v-else>
            <label class="font-weight-bold">Component Names</label><span class="text-danger">*</span>
            <div v-for="(row, index) in catalogRows" :key="row.key" class="mb-2">
              <div class="d-flex align-items-center">
                <b-form-input :ref="`catalogRow${index}`" v-model.trim="row.name" placeholder="e.g. Transport"
                  @input="row.error = null" @keyup.enter="addCatalogRow" />
                <i v-if="catalogRows.length > 1" class="fas fa-trash text-danger ml-2"
                  style="cursor: pointer;font-size: 14px !important;" @click="catalogRows.splice(index, 1)"></i>
              </div>
              <span class="input-error-message mt-1" v-if="row.error">{{ row.error }}</span>
            </div>
            <b-link class="d-inline-block mb-3" @click="addCatalogRow">
              <i class="fas fa-plus mr-1" style="font-size: 11px !important;"></i> Add another
            </b-link>
            <div>
              <b-button variant="info" :disabled="catalogSaving" @click="saveCatalogComponents">
                Save {{ filledCatalogRows.length > 1 ? `${filledCatalogRows.length} Components` : 'Component' }}
              </b-button>
            </div>
          </template>
        </div>
      </b-modal>

      <div v-if="!catalogComponents.length"
        class="coming-soon-box d-flex flex-column align-items-center justify-content-center text-center py-5">
        <i class="fas fa-info-circle fa-2x text-muted mb-2"></i>
        <span class="text-muted">No fee components defined yet.</span>
      </div>

      <div v-else class="table-responsive rounded mt-1">
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Description</th>
              <th>Used In</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(component, index) in catalogComponents" :key="component.id">
              <td>{{ index + 1 }}</td>
              <td>{{ component.name }}</td>
              <td class="text-muted">{{ component.description || '-' }}</td>
              <td>{{ component.usage_count }} structure(s)</td>
              <td class="text-right">
                <i class="fas fa-edit text-info mx-1" style="cursor: pointer;font-size: 15px !important;"
                  @click="showCatalogModal(component)"></i>
                <span v-if="component.usage_count > 0" class="tooltip-wrapper d-inline-flex align-items-center">
                  <i class="fas fa-trash text-muted mx-1" style="font-size: 15px !important;"></i>
                  <div class="custom-tooltip custom-tooltip-right">
                    <span>Used by {{ component.usage_count }} structure(s) - remove it from all of them first.</span>
                  </div>
                </span>
                <i v-else class="fas fa-trash text-danger mx-1" style="cursor: pointer;font-size: 15px !important;"
                  @click="removeCatalogComponent(component)"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

    <!-- ================= CLASS FEE STRUCTURES PAGE ================= -->
    <b-card v-else-if="activeSection === 'classes'" class="mt-4">
      <h6 class="mb-0">Class Fee Structures</h6>
      <small class="text-muted d-block mb-3">
        Every student in an assigned class is mapped to its fee structure, including students added later. Fill in the
        bill details to generate the class's bills automatically - students added later are billed the same amounts.
        Saving again with a new bill name or start date generates a new set of bills; existing bills are not changed.
      </small>

      <b-row>
        <b-col cols="12" md="3" class="mb-2">
          <b-form-select v-model="selectedDepartment" :options="departmentOptions"
            @change="classAssignForm.classes = []" />
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <multiselect v-model="classAssignForm.classes" :options="departmentClasses" :multiple="true"
            :close-on-select="false" placeholder="Select classes" label="label" track-by="id" />
        </b-col>
        <b-col cols="12" md="3" class="mb-2">
          <b-form-select v-model="classAssignForm.fee_category_id" :options="feeCategoryOptions"
            @change="loadClassAssignComponents" />
        </b-col>
        <b-col cols="12" md="2" class="mb-2">
          <b-button variant="info" block :disabled="classAssignSaving" @click="assignStructureToClasses">
            {{ classAssignSaving ? 'Assigning...' : 'Assign' }}
          </b-button>
        </b-col>
      </b-row>

      <div v-if="classAssignForm.fee_category_id" class="border rounded p-3 mt-2">
        <h6 class="mb-1">Bill Details</h6>
        <small class="text-muted d-block mb-2">
          Leave the bill name empty to only map students, without generating bills.
        </small>
        <b-row>
          <b-col cols="12" md="3" class="mb-2">
            <label>Bill Name</label>
            <b-form-input v-model.trim="classAssignForm.bill_name" placeholder="e.g. Term 1 2026" />
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <label>Start Date</label><span v-if="classAssignForm.bill_name" class="text-danger">*</span>
            <b-form-input v-model="classAssignForm.start_date" type="date" />
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <label>End Date</label>
            <b-form-input v-model="classAssignForm.end_date" type="date" />
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <label>Due Date (Pay with Fine)</label>
            <b-form-input v-model="classAssignForm.due_date" type="date" />
          </b-col>
        </b-row>

        <div v-if="classAssignForm.components.length" class="table-responsive rounded mt-2">
          <table class="table table-bordered table-sm mb-0">
            <thead class="thead-dark text-left">
              <tr>
                <th>Component</th>
                <th>Type</th>
                <th style="width: 200px;">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="component in classAssignForm.components" :key="component.fee_category_component_id">
                <td>{{ component.name }}</td>
                <td><b-badge :variant="typeBadgeVariant(component.component_type)">{{ component.component_type }}</b-badge></td>
                <td>
                  <b-form-input v-model="component.amount" type="number" step="0.01" size="sm" placeholder="Not billed" />
                </td>
              </tr>
            </tbody>
          </table>
          <small class="text-muted">Components left empty or 0 are not billed. Discounts are negative.</small>
        </div>
        <small v-else-if="classAssignForm.fee_category_id" class="text-muted d-block mt-2">
          No component in this structure allows auto mapping - tick "Allow auto mapping" on a component to bill it
          here.
        </small>
      </div>

      <div v-if="!assignedClasses.length"
        class="coming-soon-box d-flex flex-column align-items-center justify-content-center text-center py-5 mt-3">
        <i class="fas fa-info-circle fa-2x text-muted mb-2"></i>
        <span class="text-muted">No classes have a fee structure yet.</span>
      </div>

      <div v-else class="table-responsive rounded mt-3">
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <tr>
              <th>#</th>
              <th>Class</th>
              <th>Fee Structure</th>
              <th>Bill</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(feeClass, index) in assignedClasses" :key="feeClass.id">
              <td>{{ index + 1 }}</td>
              <td>{{ feeClass.label }}</td>
              <td>{{ feeClass.fee_category_name }}</td>
              <td>
                <template v-if="feeClass.bill_name">
                  {{ feeClass.bill_name }}
                  <small class="text-muted d-block">
                    {{ feeClass.start_date }}<span v-if="feeClass.due_date"> · due {{ feeClass.due_date }}</span>
                  </small>
                </template>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="text-right">
                <i class="fas fa-trash text-danger mx-1" style="cursor: pointer;font-size: 15px !important;"
                  @click="unassignClass(feeClass)"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>

  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";

export default {
  data() {
    return {
      activeSection: null,
      feeCollectionMode: 'manual',
      beforeDragOrder: [],
      feeCategories: [],
      activeCategory: null,
      isEditingCategory: false,
      isEditingComponent: false,
      componentTypeOptions: [
        { value: 'fee', text: 'Fee' },
        { value: 'deposit', text: 'Deposit' },
        { value: 'fine', text: 'Fine' }
      ],
      billingFrequencyOptions: [
        { value: 'once', text: 'Once' },
        { value: 'monthly', text: 'Monthly' },
        { value: 'semester', text: 'Semester' },
        { value: 'yearly', text: 'Yearly' }
      ],
      categoryForm: {
        id: null,
        name: '',
        description: ''
      },
      categoryNameError: null,
      newCategoryNameError: null,
      newCategoryForm: {
        name: '',
        description: '',
        components: []
      },
      componentForm: {
        id: null,
        name: '',
        catalog: null,
        component_type: 'fee',
        billing_frequency: 'once',
        allow_partial_payment: false,
        allow_auto_mapping: false,
        description: '',
        fee_category_id: null
      },
      newComponentRows: [],
      componentSaving: false,
      catalogComponents: [],
      catalogForm: {
        id: null,
        name: '',
        description: ''
      },
      catalogNameError: null,
      // Add Component modal - one row per new component name
      catalogRows: [],
      catalogSaving: false,
      billingMode: null,
      feeClasses: [],
      selectedDepartment: null,
      classAssignForm: this.emptyClassAssignForm(),
      classAssignSaving: false,
    };
  },
  computed: {
    filledCatalogRows() {
      return this.catalogRows.filter((row) => row.name);
    },
    isClassBilling() {
      return this.billingMode === 'class';
    },
    departmentOptions() {
      const departments = new Map();
      this.feeClasses.forEach((feeClass) => {
        if (feeClass.usergroup_id && !departments.has(feeClass.usergroup_id)) {
          departments.set(feeClass.usergroup_id, feeClass.programme);
        }
      });
      return [
        { value: null, text: 'All departments' },
        ...[...departments].map(([value, text]) => ({ value, text }))
          .sort((a, b) => (a.text || '').localeCompare(b.text || ''))
      ];
    },
    departmentClasses() {
      if (!this.selectedDepartment) return this.feeClasses;
      return this.feeClasses.filter((feeClass) => feeClass.usergroup_id === this.selectedDepartment);
    },
    assignedClasses() {
      return this.departmentClasses.filter((feeClass) => feeClass.assignment_id);
    },
    feeCategoryOptions() {
      return [
        { value: null, text: 'Select a fee structure' },
        ...this.feeCategories.map((category) => ({ value: category.id, text: category.name }))
      ];
    },
    // Catalog components the structure being added to doesn't have yet
    catalogNotInActiveCategory() {
      const existing = new Set((this.activeCategory?.components || []).map((c) => c.fee_component_id));
      return this.catalogComponents.filter((item) => !existing.has(item.id));
    },
    feeCategoryComponentRows() {
      const rows = [];
      this.feeCategories.forEach((category, categoryIndex) => {
        const components = category.components;
        if (!components.length) {
          rows.push({
            key: `${category.id}-empty`,
            category,
            categoryIndex: categoryIndex + 1,
            isFirstOfCategory: true,
            rowspan: 1,
            component: null
          });
          return;
        }
        components.forEach((component, componentIndex) => {
          rows.push({
            key: `${category.id}-${component.id}`,
            category,
            categoryIndex: categoryIndex + 1,
            isFirstOfCategory: componentIndex === 0,
            rowspan: components.length,
            component
          });
        });
      });
      return rows;
    }
  },
  validations: {
    categoryForm: {
      name: { required }
    },
    componentForm: {
      component_type: { required },
      billing_frequency: { required }
    }
  },
  async created() {
    await this.loadFeeCategories();
    await this.loadCollectionMode();
    await this.loadCatalogComponents();
    await this.loadFeeClasses();
  },
  methods: {
    goBack() {
      if (this.activeSection) {
        this.activeSection = null;
        return true;
      }
      return false;
    },
    async loadCollectionMode() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/institution/setting/fee_collection_mode`;
      const response = await this.$axios.get(url);
      this.feeCollectionMode = response.data || 'manual';
    },
    async saveCollectionMode(mode) {
      const url = this.$store.getters.getAPIKey.mainAPI + `/institution/setting/fee_collection_mode`;
      await this.$axios.post(url, { fee_collection_mode: mode });
      this.$toast.success("Fee collection mode updated");
    },
    async loadFeeCategories() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories`;
      const response = await this.$axios.get(url);
      this.feeCategories = response.data;
    },
    showCategoryModal(category) {
      this.isEditingCategory = !!category;
      this.categoryNameError = null;
      this.newCategoryNameError = null;
      if (category) {
        this.categoryForm = { id: category.id, name: category.name, description: category.description || '' };
        this.$v.categoryForm.$reset();
      } else {
        this.newCategoryForm = { name: '', description: '', components: [] };
      }
      this.$bvModal.show('CategoryModal');
    },
    async saveCategory() {
      this.$v.categoryForm.$touch();
      if (this.$v.categoryForm.$invalid) return;

      const { id, name, description } = this.categoryForm;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories`;

      this.categoryNameError = null;
      try {
        await this.$axios.put(`${url}/${id}`, { name, description });
      } catch (error) {
        this.categoryNameError = error.response?.data?.errors?.name?.[0]
          || error.response?.data?.message
          || "Something went wrong, please try again!";
        return;
      }
      this.$toast.success("Category updated successfully");

      this.$bvModal.hide('CategoryModal');
      this.loadFeeCategories();
    },
    isCatalogPicked(rows, item) {
      return rows.some((row) => row.catalog.id === item.id);
    },
    // Ticking a catalog component adds a details box for it; unticking removes it.
    toggleCatalogPick(rows, item, checked) {
      if (checked) {
        rows.push({
          catalog: item,
          component_type: 'fee',
          billing_frequency: 'once',
          allow_partial_payment: false,
          allow_auto_mapping: false
        });
      } else {
        const index = rows.findIndex((row) => row.catalog.id === item.id);
        if (index !== -1) rows.splice(index, 1);
      }
    },
    validateNewCategoryForm() {
      if (!this.newCategoryForm.name) {
        this.$toast.error("Category name is required");
        return false;
      }
      return true;
    },
    async saveNewCategoryWithComponents() {
      if (!this.validateNewCategoryForm()) return;

      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories/with-components`;
      const payload = {
        name: this.newCategoryForm.name,
        description: this.newCategoryForm.description,
        components: this.newCategoryForm.components.map((component) => ({
          fee_component_id: component.catalog.id,
          component_type: component.component_type,
          billing_frequency: component.billing_frequency,
          allow_partial_payment: component.allow_partial_payment,
          ...this.autoMappingPayload(component.allow_auto_mapping),
          description: ''
        }))
      };

      this.newCategoryNameError = null;
      try {
        await this.$axios.post(url, payload);
      } catch (error) {
        const nameError = error.response?.data?.errors?.name?.[0];
        if (nameError) {
          this.newCategoryNameError = nameError;
        } else {
          this.$toast.error(error.response?.data?.message || "Something went wrong, please try again!");
        }
        return;
      }
      this.$toast.success("Category added successfully");

      this.$bvModal.hide('CategoryModal');
      this.loadFeeCategories();
      this.loadCatalogComponents();
    },
    async removeCategory(id) {
      if (!confirm("Delete this structure?")) return;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories/${id}`;
      await this.$axios.delete(url);
      this.$toast.success("Structure deleted");
      this.loadFeeCategories();
    },
    showComponentModal(category, component) {
      this.activeCategory = category;
      this.isEditingComponent = !!component;
      if (component) {
        this.componentForm = {
          id: component.id,
          name: component.name,
          catalog: null,
          component_type: component.component_type,
          billing_frequency: component.billing_frequency,
          allow_partial_payment: !!component.allow_partial_payment,
          allow_auto_mapping: !!component.allow_auto_mapping,
          description: component.description || '',
          fee_category_id: category.id
        };
        this.$v.componentForm.$reset();
      } else {
        this.newComponentRows = [];
      }
      this.$bvModal.show('ComponentModal');
    },
    // Saves each row with the single-component endpoint. A saved row is
    // dropped from the list right away, so if a later one fails the user can
    // fix it and retry without re-adding the ones that already went through.
    async saveNewComponents() {
      if (!this.newComponentRows.length) {
        this.$toast.error("Select at least one component");
        return;
      }

      const url = this.$store.getters.getAPIKey.mainAPI + `/feecategories/${this.activeCategory.id}/feecomponents`;
      this.componentSaving = true;
      try {
        while (this.newComponentRows.length) {
          const component = this.newComponentRows[0];
          try {
            await this.$axios.post(url, {
              fee_component_id: component.catalog.id,
              component_type: component.component_type,
              billing_frequency: component.billing_frequency,
              allow_partial_payment: component.allow_partial_payment,
              ...this.autoMappingPayload(component.allow_auto_mapping),
              description: ''
            });
          } catch (error) {
            this.$toast.error(`${component.catalog.name}: ` +
              (error.response?.data?.message || "Something went wrong, please try again!"));
            return;
          }
          this.newComponentRows.shift();
        }
      } finally {
        this.componentSaving = false;
        this.loadFeeCategories();
        this.loadCatalogComponents();
      }

      this.$toast.success("Components added");
      this.$bvModal.hide('ComponentModal');
    },
    async saveComponent() {
      this.$v.componentForm.$touch();
      if (this.$v.componentForm.$invalid) return;

      const { id, component_type, billing_frequency, allow_partial_payment, allow_auto_mapping, description, fee_category_id } = this.componentForm;
      const url = this.$store.getters.getAPIKey.mainAPI;
      const payload = {
        component_type, billing_frequency, allow_partial_payment, description,
        ...this.autoMappingPayload(allow_auto_mapping)
      };

      try {
        await this.$axios.put(`${url}/feecomponents/${id}`, { ...payload, fee_category_id });
        this.$toast.success("Component updated");
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Something went wrong, please try again!");
        return;
      }

      this.$bvModal.hide('ComponentModal');
      this.loadFeeCategories();
      this.loadCatalogComponents();
    },
    async removeComponent(categoryId, componentId) {
      if (!confirm("Delete this component?")) return;
      const url = this.$store.getters.getAPIKey.mainAPI +
        `/feecategories/${categoryId}/feecomponents/${componentId}`;
      await this.$axios.delete(url);
      this.$toast.success("Component deleted");
      this.loadFeeCategories();
      this.loadCatalogComponents();
    },
    async loadCatalogComponents() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecomponents/catalog`;
      const response = await this.$axios.get(url);
      this.catalogComponents = response.data;
    },
    showCatalogModal(component) {
      this.catalogNameError = null;
      this.catalogForm = component
        ? { id: component.id, name: component.name, description: component.description || '' }
        : { id: null, name: '', description: '' };
      this.catalogRows = [this.newCatalogRow()];
      this.$bvModal.show('CatalogComponentModal');
    },
    newCatalogRow() {
      return { key: `${Date.now()}-${Math.random()}`, name: '', error: null };
    },
    addCatalogRow() {
      this.catalogRows.push(this.newCatalogRow());
      const index = this.catalogRows.length - 1;
      this.$nextTick(() => {
        const input = this.$refs[`catalogRow${index}`];
        if (input && input[0]) input[0].focus();
      });
    },
    // Creates each named row through the single-create endpoint. A row that
    // saves is removed, so after a failure (e.g. a duplicate name) only the
    // unsaved ones are left to fix and retry.
    async saveCatalogComponents() {
      this.catalogRows = this.catalogRows.filter((row) => row.name);
      if (!this.catalogRows.length) {
        this.catalogRows = [this.newCatalogRow()];
        this.catalogRows[0].error = "Enter at least one component name";
        return;
      }

      const seen = new Set();
      for (const row of this.catalogRows) {
        const key = row.name.toLowerCase();
        if (seen.has(key)) {
          row.error = "This name is entered more than once";
          return;
        }
        seen.add(key);
      }

      const url = this.$store.getters.getAPIKey.mainAPI + `/feecomponents/catalog`;
      let created = 0;
      this.catalogSaving = true;
      try {
        while (this.catalogRows.length) {
          const row = this.catalogRows[0];
          try {
            await this.$axios.post(url, { name: row.name });
          } catch (error) {
            row.error = error.response?.data?.errors?.name?.[0]
              || error.response?.data?.message
              || "Something went wrong, please try again!";
            return;
          }
          this.catalogRows.shift();
          created++;
        }
      } finally {
        this.catalogSaving = false;
        if (created) {
          this.loadCatalogComponents();
          this.loadFeeCategories();
        }
      }

      this.$toast.success(created === 1 ? "Component created" : `${created} components created`);
      this.$bvModal.hide('CatalogComponentModal');
    },
    async saveCatalogComponent() {
      if (!this.catalogForm.name) {
        this.catalogNameError = "Component name is required";
        return;
      }

      const { id, name, description } = this.catalogForm;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecomponents/catalog`;

      this.catalogNameError = null;
      try {
        if (id) {
          await this.$axios.put(`${url}/${id}`, { name, description });
          this.$toast.success("Component updated");
        } else {
          await this.$axios.post(url, { name, description });
          this.$toast.success("Component created");
        }
      } catch (error) {
        this.catalogNameError = error.response?.data?.errors?.name?.[0]
          || error.response?.data?.message
          || "Something went wrong, please try again!";
        return;
      }

      this.$bvModal.hide('CatalogComponentModal');
      this.loadCatalogComponents();
      this.loadFeeCategories();
    },
    async removeCatalogComponent(component) {
      if (!confirm(`Delete component "${component.name}"?`)) return;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feecomponents/catalog/${component.id}`;
      try {
        await this.$axios.delete(url);
        this.$toast.success("Component deleted");
        this.loadCatalogComponents();
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to delete component");
      }
    },
    typeBadgeVariant(type) {
      const variants = { fee: 'secondary', deposit: 'info', fine: 'warning', discount: 'danger' };
      return variants[type] || 'secondary';
    },
    formatComponentAmount(component) {
      if (component.amount === null || component.amount === '' || component.amount === undefined) {
        return 'Amount set per student';
      }
      return `₹${parseFloat(component.amount).toFixed(2)}`;
    },
    // Also tells us the institution's billing mode (set under Institution
    // Settings > Fee Billing) - class-mode-only UI (the Class Fee Structures
    // section) stays hidden for 'default' institutions.
    async loadFeeClasses() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/feeclassstructures`;
      const response = await this.$axios.get(url);
      this.billingMode = response.data.billing_mode;
      this.feeClasses = response.data.classes.map((feeClass) => ({
        ...feeClass,
        label: [feeClass.name, feeClass.programme, feeClass.academic_year].filter(Boolean).join(' - ')
      }));
    },
    emptyClassAssignForm() {
      return {
        fee_category_id: null,
        classes: [],
        bill_name: '',
        start_date: null,
        end_date: null,
        due_date: null,
        components: []
      };
    },
    // Auto mapping is only shown (and so only changed) in class billing mode.
    autoMappingPayload(allowAutoMapping) {
      return this.isClassBilling ? { allow_auto_mapping: !!allowAutoMapping } : {};
    },
    // One amount row per structure component that allows auto mapping,
    // prefilled from the structure.
    loadClassAssignComponents(feeCategoryId) {
      const category = this.feeCategories.find((c) => c.id === feeCategoryId);
      const components = (category ? category.components : []).filter((component) => component.allow_auto_mapping);
      this.classAssignForm.components = components.map((component) => ({
        fee_category_component_id: component.id,
        name: component.name,
        component_type: component.component_type,
        amount: component.signed_amount ? component.signed_amount : ''
      }));
    },
    async assignStructureToClasses() {
      const { fee_category_id, classes, bill_name, start_date, end_date, due_date, components } = this.classAssignForm;
      if (!fee_category_id || !classes.length) {
        this.$toast.error("Select a fee structure and at least one class");
        return;
      }
      if (bill_name && !start_date) {
        this.$toast.error("Enter a start date for the bill");
        return;
      }

      const reassigned = classes.filter((feeClass) => feeClass.assignment_id && feeClass.fee_category_id !== fee_category_id);
      if (reassigned.length && !confirm(
        `${reassigned.length} selected class(es) already have a different structure. Students will be moved to the new ` +
        `structure - bills they already have are kept. Continue?`
      )) return;

      const url = this.$store.getters.getAPIKey.mainAPI + `/feeclassstructures`;
      this.classAssignSaving = true;
      try {
        const response = await this.$axios.post(url, {
          fee_category_id,
          usersubgroup_ids: classes.map((feeClass) => feeClass.id),
          bill_name: bill_name || null,
          start_date: start_date || null,
          end_date: end_date || null,
          due_date: due_date || null,
          components: components
            .filter((component) => component.amount !== '' && component.amount !== null)
            .map(({ fee_category_component_id, amount }) => ({ fee_category_component_id, amount }))
        });
        this.$toast.success(response.data.message);
        this.classAssignForm = this.emptyClassAssignForm();
        this.loadFeeClasses();
        this.loadFeeCategories();
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Failed to assign structure");
      } finally {
        this.classAssignSaving = false;
      }
    },
    async unassignClass(feeClass) {
      if (!confirm(`Remove the fee structure from "${feeClass.name}"? Students already mapped keep their structure and bills.`)) return;
      const url = this.$store.getters.getAPIKey.mainAPI + `/feeclassstructures/${feeClass.assignment_id}`;
      await this.$axios.delete(url);
      this.$toast.success("Class unassigned");
      this.loadFeeClasses();
    },
    async saveComponentOrder(category) {
      const newOrder = category.components.map((c) => c.id);
      const unchanged = newOrder.length === this.beforeDragOrder.length &&
        newOrder.every((id, index) => id === this.beforeDragOrder[index]);
      if (unchanged) return;

      const url = this.$store.getters.getAPIKey.mainAPI +
        `/feecategories/${category.id}/feecomponents/reorder`;
      await this.$axios.put(url, { component_ids: newOrder });
      this.$toast.success("Collection order updated");
    }
  }
};
</script>

<style scoped>
.component-box {
  border: 1px solid #0d6efd;
  border-radius: 6px;
}

.catalog-pick {
  border: 1px dashed #0d6efd;
  border-radius: 6px;
}

.component-box-header {
  position: relative;
  text-align: center;
}

.component-box-title {
  color: #0d6efd;
}

/* Kept at the right edge so the heading stays truly centred */
.component-box-remove {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

/* Line the checkbox up with the inputs beside it */
/* Same height as the selects beside it, so the checkbox sits level with them. */
.partial-payment-check {
  min-height: 38px;
  display: flex;
  align-items: center;
}

.config-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;
}

.config-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.config-card .card-title {
  font-weight: 600;
  font-size: 1rem;
  transition: color 0.3s ease, text-decoration 0.3s ease;
}

.config-card:hover .card-title {
  color: #0d6efd;
  text-decoration: underline;
}

.coming-soon-box {
  border: 1px dashed #dee2e6;
  border-radius: 12px;
  background: #f9f9fb;
}

.order-tile {
  border-radius: 16px;
  border: none;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.06);
}

.order-item {
  background: #f9f9fb;
  border: 1px solid #eee;
  border-radius: 8px;
}

.drag-handle {
  cursor: grab;
}

.form-group-error {
  border: 1px solid #dc3545 !important;
}

.input-error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: -10px;
  margin-bottom: 10px;
  display: block;
}

.tooltip-wrapper {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.custom-tooltip {
  visibility: hidden;
  background-color: #000;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  position: absolute;
  z-index: 999;
  /* Opens above the icon so it stays inside the table - the table box
     hides vertical overflow, so one below the last row would be cut off. */
  bottom: 100%;
  left: 0;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
  margin-bottom: 5px;
  font-size: 13px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
}

/* For icons in the last column - grows leftward. Even while hidden, a
   tooltip sticking out past the table's right edge gives it a horizontal
   scrollbar. */
.custom-tooltip-right {
  left: auto;
  right: 0;
}

.tooltip-wrapper:hover .custom-tooltip {
  visibility: visible;
  opacity: 1;
}
</style>
